import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaSearch,
  FaDownload,
  FaBan,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import dayjs from "dayjs";
import { useNavigate } from 'react-router-dom';
import VendorFormModal from "./VendorFormModal";
import VendorViewModal from "./VendorViewModal";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from '../components/useScreenPermissions';

const API_URL = "/api";

const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const [showView, setShowView] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState("All");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
    // Add this line
  const [isSyncing, setIsSyncing] = useState(false);

  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [syncData, setSyncData] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // --- THEME CSS VARIABLES FOR SIDEBAR & GLOBAL THEME ---
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",          // keep sidebar background white
      "--sidebar-text": "#0f1724",        // dark text for sidebar
      "--accent-color": "#2b8f85",        // Create button color (accent)
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#9aa7b0",
      "--search-icon": "#cbd5da",
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      "--status-active-bg-1": "#d2f6f0ff",
      "--status-inactive-bg-1": "#ffb89bff",
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    const styleId = "vendor-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .primary-btn { background-color: var(--accent-color); color: #ffffff; border-radius: 0; }
        .primary-btn:hover { background-color: var(--accent-hover); }

        .secondary-btn { background-color: #ffffff; color: var(--text-primary); border: 1px solid var(--page-border-color); border-radius: 0; }
        .secondary-btn:hover { background-color: #f8faf9; }

        /* make status text bold by using font-weight */
        .status-active { background-color: var(--status-active-bg-1); color: var(--status-active-color); border-radius: 6px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }
        .status-active:hover { filter: brightness(0.98); }

        .status-active-1 { background-color: var(--status-active-bg-1); color: var(--status-active-color); border-radius: 16px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }
        .status-inactive-1 { background-color: var(--status-inactive-bg-1); color: var(--status-inactive-color); border-radius: 16px; padding: 4px 10px; font-size: 12px; display: inline-block; font-weight: 600; }
        .status-inactive:hover { filter: brightness(0.98); }

        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        .cust-status{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          
          }

          .cust-status-in{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

        /* stronger active/inactive arrow colors */
        .sort-active { color: #155e52 !important; }
        .sort-inactive { color: var(--sort-inactive) !important; }

        .page-active { background-color: var(--accent-color); color: #fff; border-radius: 0; }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        /* --- Header sort: tighter spacing and darker arrows like screenshot --- */
        .vendor-table-header-cell { display: flex; align-items: center; justify-content: center; gap: 1px; }
        .table-header-label { font-weight: 700; color: #455a64; letter-spacing: 0.02em; font-size: 12px; }

        /* make arrow box compact and arrows stacked vertically */
        .table-header-arrow-box { width: 18px; display: flex; align-items: center; justify-content: center; }
        .table-header-sort { display: flex; flex-direction: column; align-items: center; gap: 0px; }
        .table-header-sort svg { width: 10px; height: 10px; margin: -4px 0; }

        /* slightly darker inactive arrows for better visibility */
        .table-header-sort .sort-inactive { opacity: 0.85; color: var(--sort-inactive); }
        .table-header-sort .sort-active { opacity: 1; color: #155e52; }

        .icon-btn { transition: color .15s ease; }

        .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
        .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item { color: var(--sidebar-text) !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken'); // Or sessionStorage, depending on where you saved it
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

   useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

 const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/vendors`,{headers: getAuthHeaders()});
      const data = await res.json();
      setVendors(data);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setVendors([]);
    }
    setLoading(false);
  };
   const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    
    // Sort the vendors array
    const sortedVendors = [...vendors].sort((a, b) => {
      let valA, valB;

      // Handle Address sorting (Nested Object)
      if (key === "address") {
        valA = a.addresses?.[0]?.address_line1 || "";
        valB = b.addresses?.[0]?.address_line1 || "";
      } 
      // Handle Contact Phone sorting (Nested Object)
      else if (key === "contact_phone") {
        // Use the same priority as the table display: nested first, then top-level
        valA = a.contacts?.[0]?.phone_number || a.contact_phone || "";
        valB = b.contacts?.[0]?.phone_number || b.contact_phone || "";
      } 
      // Handle date fields
      else if (key === "created_at") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      } 
      // Handle Vendor Code (Numeric Parsing)
      else if (key === "vendor_code") {
        return direction === "asc"
          ? a[key].localeCompare(b[key], undefined, { numeric: true, sensitivity: 'base' })
          : b[key].localeCompare(a[key], undefined, { numeric: true, sensitivity: 'base' });
      } 
      // Default for other fields (e.g., vendor_name)
      else {
        if (a[key] === null) return 1;
        if (b[key] === null) return -1;
        valA = a[key];
        valB = b[key];
      }

      // String comparison for the extracted values
      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });
    
    setVendors(sortedVendors);
  };

  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="table-header-sort" aria-hidden>
        <FaSortUp className={`${active && sortConfig.direction === 'asc' ? 'sort-active' : 'sort-inactive'}`} />
        <FaSortDown className={`${active && sortConfig.direction === 'desc' ? 'sort-active' : 'sort-inactive'}`} />
      </span>
    );
  };

 
  const handleView = (vendor) => {
    setSelectedVendor(vendor);
    setShowView(true);
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      const response = await fetch(`${API_URL}/vendors/${vendor.vendor_code}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete vendor");
      }
      fetchVendors();
    } catch (error) {
      console.error("Error deleting vendor:", error);
      alert(error.message || "An error occurred while deleting the vendor");
    }
  };

  // Deactivate function with separate endpoint
  const handleDeactivate = async (vendor) => {
    if (!window.confirm("Are you sure you want to deactivate this vendor?"))
      return;
    try {
      console.log("Deactivating vendor:", vendor.vendor_code);
      const response = await fetch(
        `${API_URL}/vendors/${vendor.vendor_code}/deactivate`,
        {
          method: "PUT",
         headers: getAuthHeaders()
        }
      );
      console.log("Deactivate response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Deactivate error:", errorData);
        throw new Error(errorData.message || "Failed to deactivate vendor");
      }
      // Success message
      const result = await response.json();
      console.log("Deactivate result:", result);
      // Refresh vendor list
      fetchVendors();
    } catch (error) {
      console.error("Error deactivating vendor:", error);
      alert(error.message || "An error occurred while deactivating the vendor");
    }
  };

  // Reactivate function
  const handleReactivate = async (vendor) => {
    if (!window.confirm("Are you sure you want to reactivate this vendor?"))
      return;
    try {
      console.log("Reactivating vendor:", vendor.vendor_code);
      const response = await fetch(
        `${API_URL}/vendors/${vendor.vendor_code}/reactivate`,
        {
          method: "PUT",
          headers: getAuthHeaders()
        }
      );
      console.log("Reactivate response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Reactivate error:", errorData);
        throw new Error(errorData.message || "Failed to reactivate vendor");
      }
      // Success message
      const result = await response.json();
      console.log("Reactivate result:", result);
      // Refresh vendor list
      fetchVendors();
    } catch (error) {
      console.error("Error reactivating vendor:", error);
      alert(error.message || "An error occurred while reactivating the vendor");
    }
  };

  const handleCreate = () => {
    setSelectedVendor(null);
    setIsEdit(false);
    setShowForm(true);
  };

     const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    
    try {
      const res = await fetch(`${API_URL}/mapping/bc-vendors/sync`, { method: 'GET',headers: getAuthHeaders() });
      const data = await res.json();

      if (data.success) {
        // --- UPDATE THIS SECTION ---
        // Store the data and open the modal
        setSyncData({
          total: data.recordCount,
          inserted: data.inserted,
          updated: data.updated
        });
        setShowSyncSuccess(true);
        // ---------------------------

        fetchVendors(); 
      } else {
        alert(`Sync Failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Sync Error:", error);
      alert("An error occurred while syncing vendors.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      "vendor_code",
      "vendor_name",
      "contact_phone",
      "address",
      "created_at",
    ];
    
    const rows = vendors.map((v) => [
      v.vendor_code,
      v.vendor_name,
      v.contact_phone || '',
      v.addresses?.[0]?.address_line1 || '',
      
      dayjs(v.created_at).format("YYYY-MM-DD HH:mm"),
    ]);
    
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendors.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFormSubmit = async (formData) => {
    setShowForm(false);
    fetchVendors();
  };

   const handleLogout = () => {
    navigate('/login');
  };

    const filteredVendors = vendors.filter((vendor) => {
    // Convert searchTerm once to lowercase for efficiency
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    const matchesSearch =
      searchTerm === "" ||
      (vendor.vendor_code || "").toLowerCase().includes(lowerSearchTerm) ||
      (vendor.vendor_name || "").toLowerCase().includes(lowerSearchTerm) ||
      (vendor.contact_email && vendor.contact_email.toLowerCase().includes(lowerSearchTerm)) ||
      
      // --- UPDATED CONTACT SEARCH ---
      // Searches the nested contact array (displayed in table) AND the top-level field
      (vendor.contacts?.[0]?.phone_number && vendor.contacts[0].phone_number.toLowerCase().includes(lowerSearchTerm)) ||
      (vendor.contact_phone && vendor.contact_phone.toLowerCase().includes(lowerSearchTerm)) ||
      
      (vendor.contact_first_name && vendor.contact_first_name.toLowerCase().includes(lowerSearchTerm)) ||
      (vendor.contact_last_name && vendor.contact_last_name.toLowerCase().includes(lowerSearchTerm)) ||
      (vendor.country || "").toLowerCase().includes(lowerSearchTerm) ||
      
      // --- NEW ADDRESS SEARCH ---
      // Searches the nested address array (displayed in table)
      (vendor.addresses?.[0]?.address_line1 && vendor.addresses[0].address_line1.toLowerCase().includes(lowerSearchTerm));
      
    const matchesCategory =
      category === "All" || vendor.vendor_type === category;
      
    const matchesStatus =
      status === "All" ||
      (status === "Active" && vendor.is_active) ||
      (status === "Inactive" && !vendor.is_active);
      
    return matchesSearch && matchesCategory && matchesStatus;
  });


  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const vendorsForPage = filteredVendors.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const buttons = [];
    const maxVisible = 3;
    const start = Math.max(1, Math.min(currentPage - Math.floor(maxVisible / 2), Math.max(1, totalPages - maxVisible + 1)));
    const end = Math.min(totalPages, start + maxVisible - 1);

    buttons.push(
      <button key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      const isActive = i === currentPage;
      buttons.push(
        <button key={i} onClick={() => handlePageChange(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${isActive ? 'page-active' : 'page-inactive'}`}>
          {i}
        </button>
      );
    }

    buttons.push(
      <button key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}>
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  

  const canCreate = hasFullAccess || hasPermission('VENDOR_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('VENDOR_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('VENDOR_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('VENDOR_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Theme object passed to Sidebar so it matches vendor file theme (logout/profile icons + active item match file colors)
  const sidebarTheme = {
    text: 'var(--sidebar-text, #0f1724)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    // these are the three keys Sidebar reads for coloring active/profile/logout
    activeBg: 'var(--accent-color, #2b8f85)',   // active item background = same as Create button
    profileBg: 'var(--accent-color, #2b8f85)',  // profile/logout background
    activeText: '#ffffff',                      // text on active background
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} 
      onLogout={handleLogout} />

      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Vendor Master</h1>
            <p className="mt-1 text-base text-gray-600">Manage vendor relationships and information</p>
          </div>

           {/* --- NEW SYNC BUTTON --- */}
            <button 
              onClick={handleSync} 
              disabled={isSyncing}
              className={`flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm border ${
                isSyncing 
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
              }`}
            >
              {isSyncing ? (
                 <>
                   {/* Simple Spinner SVG */}
                   <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   Syncing...
                 </>
              ) : (
                 <>
                   <FaCheck className="h-4 w-4" />
                   Sync 
                 </>
              )}
            </button>

          <div className="flex items-center gap-3">
            <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium transition-all duration-200 shadow-sm">
              <FaDownload className="h-4 w-4" />
              Export 
            </button>

            {/* {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm rounded-none">
                <FaPlus className="h-4 w-4" />
                Create Vendor
              </button>
            )} */}
          </div>
        </header>

        {/* <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[{ label: 'Total Vendors', value: vendors.length, icon: 'users', colorClass: 'bg-blue-100 text-blue-600' }, { label: 'Active Vendors', value: vendors.filter(v => v.is_active).length, icon: 'check', colorClass: 'bg-green-100 text-green-600' }, { label: 'Inactive Vendors', value: vendors.filter(v => !v.is_active).length, icon: 'clock', colorClass: 'bg-amber-100 text-amber-600' }, { label: 'This Month', value: vendors.filter(v => { const createdDate = new Date(v.created_at); const now = new Date(); return (createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear()); }).length, icon: 'calendar', colorClass: 'bg-purple-100 text-purple-600' }].map((stat) => (
            <div key={stat.label} className="p-5 bg-white border border-gray-300 shadow-md hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 flex items-center justify-center ${stat.colorClass}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5v14" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </section> */}

        <section className="p-5 bg-white border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Search Vendors</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search by code, name, contact, country..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 pl-10 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200" />
              </div>
            </div>

            {/* <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2 bg-white border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] transition-all duration-200">
                <option>All</option>
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div> */}

            <button className="px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm" onClick={() => setCurrentPage(1)}>Search</button>
          </div>
        </section>

        <section className="bg-white border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300">
            <h2 className="text-xl font-semibold text-gray-900">Vendor List</h2>
            <div className="flex gap-2">{renderPagination()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : vendorsForPage.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No vendors found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: 'var(--table-header-bg)' }} className="text-gray-600 text-xs font-semibold uppercase">
                  <tr>
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('vendor_code')}>
                      <div className="vendor-table-header-cell flex-row" style={{ gap: '2px' }}>
                        <span className="table-header-label">Vendor Code</span>
                        <span className="table-header-arrow-box">{renderSortArrows('vendor_code')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('vendor_name')}>
                      <div className="vendor-table-header-cell flex-row">
                        <span className="table-header-label">Vendor Name</span>
                        <span className="table-header-arrow-box">{renderSortArrows('vendor_name')}</span>
                      </div>
                    </th>
                    {/* <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('vendor_type')}>
                      <div className="vendor-table-header-cell">
                        <span className="table-header-label">Vendor Type</span>
                        <span className="table-header-arrow-box">{renderSortArrows('vendor_type')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('contact_email')}>
                      <div className="vendor-table-header-cell">
                        <span className="table-header-label">Contact Email</span>
                        <span className="table-header-arrow-box">{renderSortArrows('contact_email')}</span>
                      </div>
                    </th> */}
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('contact_phone')}>
                      <div className="vendor-table-header-cell flex-row">
                        <span className="table-header-label">Contact Phone</span>
                        <span className="table-header-arrow-box">{renderSortArrows('contact_phone')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('contact_phone')}>
                      <div className="vendor-table-header-cell flex-row">
                        <span className="table-header-label">Address</span>
                        <span className="table-header-arrow-box">{renderSortArrows('address')}</span>
                      </div>
                    </th>
                    {/* <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('country')}>
                      <div className="vendor-vendor-table-header-cell">
                        <span className="table-header-label">Country</span>
                        <span className="table-header-arrow-box">{renderSortArrows('country')}</span>
                      </div>
                    </th>
                    <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('is_active')}>
                      <div className="vendor-vendor-table-header-cell">
                        <span className="table-header-label">Status</span>
                        <span className="table-header-arrow-box">{renderSortArrows('is_active')}</span>
                      </div>
                    </th> */}
                    {/* <th className="px-3 py-3 text-left cursor-pointer hover:opacity-90 transition-all duration-200" onClick={() => handleSort('created_at')}>
                      <div className="vendor-vendor-table-header-cell">
                        <span className="table-header-label">Created at</span>
                        <span className="table-header-arrow-box">{renderSortArrows('created_at')}</span>
                      </div>
                    </th> */}
                    <th className="px-3 py-3 text-center flex-row">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-300">
                  {vendorsForPage.map((vendor) => (
                    <tr key={vendor.vendor_code} className="hover:bg-gray-50 transition-colors duration-200 text-left">
                      <td className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap">
                        <p className="cursor-pointer" onClick={() => handleView(vendor)}>{vendor.vendor_code}</p>
                      </td>
                      <td className="px-6 py-3 text-gray-600">{vendor.vendor_name}</td>
                      {/* <td className="px-6 py-3 text-gray-600">{vendor.vendor_type}</td>
                      <td className="px-6 py-3 break-all max-w-[220px] text-gray-600">{vendor.contact_email || '-'}</td> */}
                      <td className="px-6 py-3 break-all max-w-[180px] text-gray-600">
  {vendor.contacts?.[0]?.phone_number || vendor.contact_phone || '-'}
</td>
<td className = "px-6 py-3 text-gray-600">{vendor.addresses?.[0]?.address_line1 || '-'}</td>
                      {/* <td className="px-6 py-3 text-gray-600 text-center">{vendor.country}</td>
                      <td className="px-6 py-3">
                        <span onClick={() => vendor.is_active ? handleDeactivate(vendor) : handleReactivate(vendor)}
                         className= {`  rounded-full ${vendor.is_active ? 'cust-status bg-teal-50 text-teal-700 hover:bg-teal-100 font-bold' : 'cust-status-in bg-amber-50 text-amber-700 font-bold hover:bg-amber-100 '}`}>
                          {vendor.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td> */}
                      {/* <td className="px-6 py-3 whitespace-nowrap">
                        {vendor.created_at ? (
                          <div className="flex flex-col">
                            <span className="text-gray-900">{dayjs(vendor.created_at).format("DD MMM YYYY")}</span>
                            <span className="text-xs text-gray-600">{dayjs(vendor.created_at).format("HH:mm:ss")}</span>
                          </div>
                        ) : "-"}
                      </td> */}
                      <td className="px-6 py-3">
                        <div className="flex gap-3 justify-center">
                          {canView && (
                            <button onClick={() => handleView(vendor)} title="View Details" className="icon-btn accent-icon-hover">
                              <FaEye className="h-4 w-4" />
                            </button>
                          )}
                          {/* {canEdit && (
                            <button onClick={() => handleEdit(vendor)} title="Edit Vendor" className="icon-btn accent-icon-hover">
                              <FaEdit className="h-4 w-4" />
                            </button>
                          )}
                          {canEdit && (
                            vendor.is_active ? (
                              <button title="Deactivate" className="icon-btn" onClick={() => handleDeactivate(vendor)} style={{ color: "#ef4444" }}>
                                <FaBan className="h-4 w-4" />
                              </button>
                            ) : (
                              <button title="Reactivate" className="icon-btn accent-icon-hover" onClick={() => handleReactivate(vendor)}>
                                <FaCheck className="h-4 w-4" />
                              </button>
                            )
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

                {/* --- NEW SYNC SUCCESS MODAL --- */}
        {showSyncSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6 text-center transform transition-all scale-100">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <FaCheck className="h-8 w-8 text-green-600" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
              <p className="text-gray-500 mb-6">The vendor data has been successfully synchronized.</p>

             

              <button
                onClick={() => setShowSyncSuccess(false)}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white primary-btn shadow-sm hover:shadow focus:outline-none transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        )}
        {/* --- END SYNC SUCCESS MODAL --- */}

       {/* Modals */}
        {showForm && (
          <VendorFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedVendor}
            isEdit={isEdit}
            loading={formLoading}
          />
        )}
        {showView && selectedVendor && (
          <VendorViewModal
            open={showView}
            onClose={() => setShowView(false)}
            vendorCode={selectedVendor.vendor_code} // Pass vendorCode instead of the whole vendor object
          />
        )}
      </main>
    </div>
  );
};

export default VendorMaster;
